import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { Button, BorderButton } from '@/components/atoms/button'
import { Divider } from '@/components/atoms/divider'
import { SelectBox } from '@/components/atoms/select-box'
import { DocumentUploadSlot } from '@/components/molecules/checkIn/document-upload-slot'
import { DocumentSlotList } from '@/components/molecules/checkIn/document-slot-list'
import { RetakeFailureDisplay } from '@/components/molecules/checkIn/identity-verify-error-dialog'
import { IdentityVerifyCaptureLayer } from '@/components/organisms/checkIn/identity-verify-capture-layer'
import { IdentificationFormProps } from '@/components/organisms/checkIn/identification-form-props'
import { DocumentCameraPreset, DocumentSlotDefinition, useDocumentSlots } from '@/hooks/use-document-slots'
import { setSelectedNationality } from '@/features/checkIn/identification-slice'
import { detectPassport, preparePassportDetector, terminatePassportDetector } from '@/libs/passport-detect'
import { countryList } from '@/libs/nationality'
import { guestsOptions } from '@/utils/form/options'
import { FormLayout } from '@/components/template/form-layout'
import { spacerTopStyle } from '@/styles/common'
import { formButtonStyle } from '@/styles/form'
import { css } from '@emotion/react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const RepresentativeSlotId = 'overseas-representative'
const companionSlotId = (index: number) => `overseas-companion-${index}`

// 宿泊人数はあとから減らせるため、「今の人数で出る枠」ではなく「これまでに作られ得た枠」を全部挙げる。
// ここから漏れた枠の画像は破棄されず、登録に混ざる
const everyPossibleSlotId = [
  RepresentativeSlotId,
  ...Array.from({ length: guestsOptions.length - 1 }, (_, index) => companionSlotId(index)),
]

const PassportSampleImageUrl: string = require('@/static/images/checkIn/passport-sample.svg').default

const PassportSlotHeightPx = 226

const PassportCaptureGuideImageUrl: string = require('@/static/images/checkIn/passport-capture-guide.svg').default

const passportCaptureHintEmphasisStyle = css({
  fontWeight: 'bold',
})

export const OverseasResidentForm: React.FC<IdentificationFormProps> = ({ hotelId, requiredIdentifyVerification, isSkippable }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useAppDispatch()

  const images = useAppSelector(state => state.checkIn.identification.images)
  const selectedNationality = useAppSelector(state => state.checkIn.identification.selectedNationality)
  const nationalitySource = useAppSelector(state => state.checkIn.identification.nationalitySource)
  const guests = useAppSelector(state => state.checkIn.checkInData.fields.guests) ?? 1
  const design = useAppSelector(state => state.design)

  const maxCompanionCount = Math.max(guests - 1, 0)

  // Divider 既定の色はページ背景色で、カード（白）の上ではほとんど見えないため form.borderColor を使う
  const sectionDividerStyle = useMemo(
    () =>
      css({
        margin: '2rem -2rem',
        borderBottomColor: design?.form.borderColor,
      }),
    [design],
  )

  const nationalityHintStyle = useMemo(
    () =>
      css({
        marginTop: '0.5rem',
        fontSize: '12px',
        color: design?.text.headlineColor,
      }),
    [design],
  )

  const [companionCount, setCompanionCount] = useState(maxCompanionCount)

  useEffect(() => {
    // OpenCV（WASM 10MB）の解析・展開は同期で 1〜2 秒画面が止まるため、
    // カメラを開く前にフォームを読んでいる間へ先読みしておく
    preparePassportDetector().catch(() => undefined)

    return () => {
      terminatePassportDetector().catch(() => undefined)
    }
  }, [])

  // 代表者のパスポートが登録されるたびに国籍を入れ替える。読み取れなかったときは空に戻す
  // （前回の読み取り値を残さない）。ゲストが手で選んだ値は上書きしない
  const representativeObjectKey = images[RepresentativeSlotId]?.objectKey
  useEffect(() => {
    if (!representativeObjectKey || nationalitySource === 'manual') {
      return
    }
    // 判定 API は旅券の MRZ から 3 文字を取るだけなので、一覧に無いコードが返ることがある。
    // そのまま入れると、セレクトは空欄なのに「読み取りました」と出て、その値が登録に回る
    const scanned = images[RepresentativeSlotId]?.nationality
    const isKnown = !!scanned && countryList(language).some(({ value }) => value === scanned)
    dispatch(setSelectedNationality({ nationality: isKnown ? scanned : undefined, source: 'scan' }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [representativeObjectKey])

  const retakeDisplay: RetakeFailureDisplay = {
    heading: t('Passport reading failed.'),
    tips: {
      lead: t('The bottom two lines (below the photo)'),
      items: [t('Make sure the text is in sharp focus.'), t('Make sure no fingers are covering it.')],
    },
  }

  const cameraPreset: DocumentCameraPreset = {
    guide: { aspectRatio: '400 / 280', widthPercent: 90 },
    guideImageUrl: PassportCaptureGuideImageUrl,
    shutter: {
      variant: 'link',
      label: t('Manual capture'),
      hint: (
        <>
          {t('Please align the passport photo page within the frame. Hold still for ')}
          <br />
          <span css={passportCaptureHintEmphasisStyle}>{t('2 seconds')}</span>
          {t(' to auto-capture.')}
        </>
      ),
      illustrationUrl: require('@/static/images/checkIn/passport-capture-illustration.png'),
    },
    autoDetect: detectPassport,
  }

  const visibleSlotDefinitions: DocumentSlotDefinition[] = [
    {
      slot: { slotId: RepresentativeSlotId, idType: 'passport', staffRegisterLabel: '代表者パスポート', order: 0 },
      sampleImageUrl: PassportSampleImageUrl,
      boxHeightPx: PassportSlotHeightPx,
      cameraInstruction: '',
    },
    ...Array.from({ length: companionCount }, (_, index) => ({
      slot: {
        slotId: companionSlotId(index),
        idType: 'passport' as const,
        staffRegisterLabel: '同行者パスポート',
        order: index + 1,
      },
      label: companionCount > 1 ? `${t('Companion')} ${index + 1}` : undefined,
      sampleImageUrl: PassportSampleImageUrl,
      boxHeightPx: PassportSlotHeightPx,
      cameraInstruction: '',
    })),
  ]

  const { slotProps, slotViews, captureLayer, isReadyToProceed, next, skip } = useDocumentSlots({
    hotelId,
    requiredIdentifyVerification,
    visibleSlotDefinitions,
    everyPossibleSlotId,
    cameraPreset,
    retakeDisplay,
  })

  const representative = slotProps(RepresentativeSlotId)
  const companionViews = slotViews.filter(view => view.slotId !== RepresentativeSlotId)

  const handleCompanionCountChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanionCount(Number(event.target.value))
  }

  const handleNationalityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setSelectedNationality({ nationality: event.target.value || undefined, source: 'manual' }))
  }

  const nationalityOptions = [
    { value: '', label: t('Country name') },
    ...countryList(language).map(({ value, name }) => ({ value, label: name })),
  ]

  return (
    <div>
      <FormLayout>
        <h2 css={headingStyle}>{t('Please register passports for all guests')}</h2>
        <p css={noticeStyle}>
          {t(
            'Guests without an address in Japan are required by law to present their passport. The facility will also keep a copy of it. Registered passport images will be used to manage the guest register and to verify the identity of each guest.',
          )}
        </p>

        <p css={sectionLabelStyle}>{t('Representative')}</p>
        <DocumentUploadSlot {...representative} />

        {images[RepresentativeSlotId] && (
          <div css={spacerTopStyle}>
            <SelectBox
              id="nationality"
              name="nationality"
              label={t('Nationality')}
              value={selectedNationality ?? ''}
              options={nationalityOptions}
              arrowDefaultSelect={false}
              onChange={handleNationalityChange}
            />
            {nationalitySource === 'scan' && selectedNationality && <p css={nationalityHintStyle}>{t('Detected from your passport')}</p>}
          </div>
        )}

        <Divider customCss={sectionDividerStyle} />

        <p css={sectionLabelStyle}>{t('Companion')}</p>
        <div css={companionCountRowStyle}>
          <SelectBox
            aria-label={t('Number of companions')}
            itemCss={companionCountSelectStyle}
            name="companionCount"
            value={companionCount}
            options={Array.from({ length: maxCompanionCount + 1 }, (_, count) => ({ value: count, label: String(count) }))}
            arrowDefaultSelect={false}
            onChange={handleCompanionCountChange}
          />
          <span css={companionCountUnitStyle}>{t('People')}</span>
        </div>

        <DocumentSlotList slots={companionViews} />
      </FormLayout>

      <Button buttonCss={[spacerTopStyle, formButtonStyle]} text={t('Next')} disabled={!isReadyToProceed} onClick={next} />
      {isSkippable && <BorderButton buttonCss={[spacerTopStyle, formButtonStyle]} text={t('Skip & Present at Check-in')} onClick={skip} />}

      <IdentityVerifyCaptureLayer {...captureLayer} />
    </div>
  )
}

const headingStyle = css({
  fontSize: '16px',
  fontWeight: 'bold',
  textAlign: 'center',
})

const noticeStyle = css({
  marginTop: '0.75rem',
  fontSize: '12px',
  lineHeight: '1.5',
})

const sectionLabelStyle = css({
  marginTop: '1.5rem',
  marginBottom: '0.75rem',
  fontSize: '14px',
  fontWeight: 'bold',
  letterSpacing: '0.7px',
})

const companionCountRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
})

const companionCountSelectStyle = css({
  flex: 1,
  '.select-wrapper': {
    marginTop: 0,
  },
})

const companionCountUnitStyle = css({
  flexShrink: 0,
})
